import React, {useEffect, useMemo} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {FormItemSkeleton, Skeleton, SkeletonItem} from 'src/components/skeleton';

import ComboBox, {ComboboxItem, DefaultItem} from '@pb/kit-component-combo-box';
import Label from '@pb/kit-component-label';


import {useGetProjectCodes} from './useGetProjectCodes';
import {useGetProjectCodesEnabled} from './useGetProjectCodesEnabled';

type Props = {
    userId: number,
    accountId: number,
    projectCode?: string,
    setProjectCode: (projectCode?: string) => void,
    onFocus?: () => void,
};

export const ProjectCodeSuggest = ({userId, accountId, projectCode, setProjectCode, onFocus}: Props) => {
    const {
        isLoading: projectCodesLoading,
        loadingError: projectCodesEnabledLoadingError,
        data: projectCodes,
        loadData,
    } = useGetProjectCodes({userId});

    const {
        isLoading: projectCodesEnabledLoading,
        data: projectCodesEnabled,
    } = useGetProjectCodesEnabled({accountId});

    useEffect(() => {
        loadData();
    }, [loadData]);

    const placeholder = useMemo(() => {
        if (!projectCodes.length) {
            return 'User has no Project Codes';
        }

        if (!projectCodesEnabled) {
            return 'Project Codes disabled for the account';
        }

        return 'Select Project Code';
    }, [projectCodesEnabled, projectCodes.length]);

    return (
        <Skeleton
            shown={projectCodesLoading || projectCodesEnabledLoading}
            view={<FormItemSkeleton labelWidth={78}><SkeletonItem width={300} height={32}/></FormItemSkeleton>}>
            <ServerResponseHandler
                smallView={true}
                sectionName="Project Code Select"
                serverError={projectCodesEnabledLoadingError}>
                <div style={{width: 300}}>
                    <Label>Project Code</Label>
                    <ComboBox
                        onFocus={onFocus}
                        value={projectCode}
                        placeholder={placeholder}
                        onChange={code => setProjectCode(code !== 'default' ? code : undefined)}
                        disabled={!projectCodesEnabled || !projectCodes.length}>
                        <DefaultItem key="default" caption={placeholder}/>
                        {projectCodes.map(({description}) => (
                            <ComboboxItem key={description} value={description} caption={description}/>
                        ))}
                    </ComboBox>
                </div>
            </ServerResponseHandler>
        </Skeleton>
    );
};
